import { JSON_FORM } from '#app/constants/forms.ts'
import {
	type JSONState,
	type QuestionText,
	type State,
	type Steps,
} from '#types/form/wizard.ts'

type QuestionType = 'regular' | 'json'

interface BaseExtractFormValuesParams<T> {
	newDataToSend: { [key: string]: State<T>['formState'] }
	typeOfQuestion: QuestionType
}

export interface RegularQuestionParams<T>
	extends BaseExtractFormValuesParams<T> {
	typeOfQuestion: 'regular'
	fieldKey: Steps<T>['fieldKey']
	questionText: QuestionText[]
}

export interface JsonQuestionParams<T> extends BaseExtractFormValuesParams<T> {
	typeOfQuestion: 'json'
	fieldKey: Steps<T>['fieldKey']
	questionText: QuestionText[]
	answeredQuestionsStack: JSONState<T>['answeredQuestionsStack']
	type: JSONState<T>['type']
	questionTree: JSONState<T>['questionTree']
	id: Steps<T>['id']
}

export type ExtractFormValuesParams<T> =
	| RegularQuestionParams<T>
	| JsonQuestionParams<T>

type IntermediateQuestionType = {
	id: string
	text: string
	answer: any
	order: number
	treeOrder: number
}

export function extractFormValues<T>({
	newDataToSend,
	typeOfQuestion,
	fieldKey,
	questionText,
	...params
}: ExtractFormValuesParams<T>) {
	if (typeOfQuestion === 'regular') {
		return [
			{
				id: fieldKey,
				text: questionText.map(({ text }) => text).join(''),
				answer: newDataToSend[fieldKey as string],
			},
		]
	}

	if (typeOfQuestion === 'json') {
		const { answeredQuestionsStack, id, type, questionTree } =
			params as JsonQuestionParams<T>

		const currentQuestionTree = questionTree.find(tree => tree.id === id)

		if (currentQuestionTree) {
			return answeredQuestionsStack
				.flatMap(stackItem => {
					if (stackItem.id !== id) return []

					return stackItem.answers.map(questionId => {
						const key = `${id}-${questionId}`
						const value = newDataToSend[key]

						if (value && type === JSON_FORM) {
							const questionText =
								currentQuestionTree.tree.questions[questionId]?.text || ''

							return {
								id: key,
								text: questionText,
								answer: value,
							}
						}

						return null
					})
				})
				.filter((item): item is IntermediateQuestionType => item !== null)
				.sort((a, b) => {
					return a.treeOrder - b.treeOrder || a.order - b.order
				})
				.map(({ id, text, answer }) => ({ id, text, answer }))
		}
	}
}
